<template>
  <div class="card mb-2">
    <div class="card-body bg-secondary pt-2 pb-0 pl-1 pr-2">
      <b-form class="search-form" @submit.prevent="onSearch">
        <div class="d-flex">
          <div class="row align-items-center">
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('customer_no')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="customer_no" type="number"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('first_name')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="first_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('last_name')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="last_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('company_name')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="company_name"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('email')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="email"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('phone')">
              <input-widget :append-question="$t('Phone and Mobile')"
                            :placeholder="true" :label='false' :model="searchModel" attribute="phone"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('address_info')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="address_info"
                            :append-question="$t('Country, City/State and Address')"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('tags')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="tags"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('category_uuids')">
              <multiple-category-select-input-widget :model="searchModel" attribute="category_uuids" :label="false"
                                                     :options="{placeholder: $t('Categories'), multiple: true}"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('interest_category_uuids')">
              <multiple-category-select-input-widget :model="searchModel" attribute="interest_category_uuids"
                                                     :label="false"
                                                     :options="{placeholder: $t('Interest Categories'), multiple: true}"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('activityDateRange')">
              <b-form-group>
                <date-range-picker style="width: 100%" @update="onDateRangeApply" showWeekNumbers
                                   showDropdowns ref="picker"
                                   :locale-data="{ firstDay: 1, format: 'DD-MM-YYYY HH:mm:ss' }"
                                   v-model="dateRange">
                  <template v-slot:input="picker" style="min-width: 350px;">
                    <span v-if="!picker.startDate" class="text-muted">
                      {{ $t('Financial Activity Date') }}
                    </span>
                    <span style="font-size: 10px" v-else>
                      {{ picker.startDate | toDate }} - {{ picker.endDate | toDate }}
                    </span>
                  </template>
                </date-range-picker>
              </b-form-group>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget
                  v-if="!excludeSearchFields.includes('account_manager_uuid')" :model="searchModel" :label="false"
                  is-search attribute="account_manager_uuid" :options="{placeholder: $t('Account Manager')}"
                  @select="onUserSelect">
              </user-select-input-widget>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('post_code')">
              <input-widget :placeholder="true" :label='false'
                            :model="searchModel" attribute="post_code"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('gender')">
              <input-widget :model="searchModel" attribute="gender" value-field="value" :label="false"
                            :options="{placeholder: $t('Gender')}"
                            display-field="${item.key}" type="select2" :items="customerGenders"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('type') && enableNlRelations">
              <input-widget :model="searchModel" :label="false" attribute="type" value-field="value"
                            display-field="${item.label}" type="select2" :items="customerTypes"
                            :options="{placeholder: $t('Type')}"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('accounting_code')">
              <input-widget :model="searchModel" attribute="accounting_code" :label="false" :placeholder="true"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="created_at" type="daterangepicker" :label="false"
                            :placeholder="$t('Created At')"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="updated_at" type="daterangepicker" :label="false"
                            :placeholder="$t('Updated At')"/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onCreatedBySelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Created By')}"
                                        attribute="created_by_uuid" is-search/>
            </div>
            <div class="input-wrapper">
              <user-select-input-widget @select="onUpdatedBySelect"
                                        :model="searchModel"
                                        :label="false"
                                        :options="{placeholder: $t('Updated By')}"
                                        attribute="updated_by_uuid" is-search/>
            </div>

            <div class="input-wrapper" v-if="!excludeSearchFields.includes('client_tier')">
              <client-tier-select-input-widget :model="searchModel" attribute="client_tier" :label="false"
                                               :options="{placeholder: $t('Client Tier')}"/>
            </div>
            <div class="input-wrapper">
              <input-widget :model="searchModel" attribute="live_id" :label="false" :placeholder="$t('Live ID')"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('optin')">
              <input-widget :model="searchModel" attribute="optin" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('is_vendor')">
              <input-widget :model="searchModel" attribute="is_vendor" type="checkbox"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('identity_verification')">
              <input-widget :model="searchModel" :label="false" :options="{placeholder: $t('Identity Verification')}"
                            display-field="${item.key}" attribute="identity_verification" type="select2" value-field="value"
                            :items="identityVerificationTypes"/>
            </div>
            <div class="input-wrapper" v-if="!excludeSearchFields.includes('kyc_status_verification')">
               <input-widget :model="searchModel" :label="false" :options="{placeholder: $t('Account/KYC Verification')}"
                            display-field="${item.key}" attribute="kyc_status_verification" type="select2" value-field="value"
                            :items="kycVerificationTypes"/>
            </div>
            <div class="input-wrapper" v-permission="'view_customerbankaccount'" v-if="!excludeSearchFields.includes('exclude_from_auto_payments') && hasStripeProvider">
              <input-widget :model="searchModel" attribute="exclude_from_auto_payments" type="checkbox"/>
            </div>
            <customer-identification-filters :search-model="searchModel" :filters="identityFieldsFilters"/>
          </div>
          <div class="search-btn-wrapper text-right">
            <b-button variant="primary" @click.prevent="onResetClick">
              <font-awesome-icon icon="undo" class="icon"/>
              {{ $t('Reset') }}
            </b-button>
            <b-button type="submit" variant="primary" class="ml-2">
              <font-awesome-icon icon="search" class="icon"/>
              {{ $t('Search') }}
            </b-button>
          </div>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import {createNamespacedHelpers} from "vuex";
  import MultipleCategorySelectInputWidget from "@/core/widgets/MultipleCategorySelectInputWidget";
  import DateRangePicker from 'vue2-daterange-picker'
  import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
  import UserSelectInputWidget from "../../core/widgets/UserSelectInputWidget";
  import CustomerIdentificationFilters from "./CustomerIdentificationFilters.vue";
  import ClientTierSelectInputWidget from "@/core/widgets/ClientTierSelectInputWidget.vue";

  const {mapState, mapActions} = createNamespacedHelpers('customer');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "CustomerSearch",
    components: {
      ClientTierSelectInputWidget,
      CustomerIdentificationFilters,
      UserSelectInputWidget,
      MultipleCategorySelectInputWidget,
      InputWidget,
      DateRangePicker
    },
    props: {
      excludeSearchFields: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        dateRange: {startDate: null, endDate: null}
      }
    },
    computed: {
      ...mapState({
        searchModel: state => state.tableData[state.tableSource].searchModel,
      }),
      ...globalMapState({
        customerGenders: state => state.customerGenders,
        customerTypes: state => state.customerTypes,
        customerIdentificationTypes: state => state.customerIdentificationTypes,
        identificationFieldsFilters: state => state.identificationFieldsFilters,
        identityVerificationTypes: state => state.identityVerificationTypes,
        kycVerificationTypes: state => state.kycVerificationTypes,
        enableNlRelations: state => state.enableNlRelations,
        hasStripeProvider: state => state.hasStripeProvider,
      }),
      identityFieldsFilters() {
        let result = [];

        if (this.identificationFieldsFilters) {
          let identityFieldsFilters = this.identificationFieldsFilters.customers;
          if (identityFieldsFilters && identityFieldsFilters.length > 0) {
            result = identityFieldsFilters;
          }
        }

        return result;
      },
    },
    methods: {
      ...mapActions(['getTableData', 'updateSearchModel', 'resetTable', 'resetTableDataPaginationPage']),
      async onSearch() {
        await this.updateSearchModel(this.searchModel);
        this.resetTableDataPaginationPage();
        this.getTableData();
      },
      onResetClick() {
        this.dateRange = {startDate: null, endDate: null};
        this.searchModel.created_at = null
        this.searchModel.created_at_date_range = {startDate: null, endDate: null};
        this.searchModel.updated_at = null
        this.searchModel.updated_at_date_range = {startDate: null, endDate: null};
        this.resetTable();
      },
      onDateRangeApply() {
        this.searchModel.activityDateRange = this.dateRange
      },
      async onUserSelect(account_manager) {
        this.updateSearchModel({account_manager});
      },
      async onCreatedBySelect(created_by) {
        this.updateSearchModel({created_by});
      },
      async onUpdatedBySelect(updated_by) {
        this.updateSearchModel({updated_by});
      },
    },
    mounted() {
      if (this.searchModel && this.searchModel.activityDateRange) {
        this.dateRange = this.searchModel.activityDateRange;
      }
    }
  }
</script>

<style lang="scss" scoped>
</style>
